import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Grid,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapHoriz as MoveIcon,
  Person as PersonIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getWarehouses } from '../../services/warehouseService';
import { getLocations } from '../../services/locationService';
import { getItemByBarcode, adjustInventory, associateUpc, moveInventory } from '../../services/inventoryService';
import { getSuppliers, getSupplierParts } from '../../services/supplierService';
import { issueInventoryToTechnician } from '../../services/technicianInventoryService';
import { getTechnicians } from '../../services/technicianService';
import BundlePrintDialog from '../../components/BundlePrintDialog';

const Scan = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [allLocations, setAllLocations] = useState([]); // For move mode destination selection
  const [technicians, setTechnicians] = useState([]); // For technician issue mode
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [mode, setMode] = useState(''); // 'add', 'issue', 'move', or 'issue_to_technician'
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null); // Current scanned location
  const [destinationLocation, setDestinationLocation] = useState(''); // For move mode
  const [selectedTechnician, setSelectedTechnician] = useState(''); // For technician issue mode
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [foundPart, setFoundPart] = useState(null);
  const [showUpcDialog, setShowUpcDialog] = useState(false);
  const [upcDialogData, setUpcDialogData] = useState({
    upc: '',
    suppliers: [],
    selectedSupplier: '',
    selectedPart: '',
    supplierParts: [],
    partId: null
  });
  const [bundlePrintDialogOpen, setBundlePrintDialogOpen] = useState(false);

  // Timer for resetting mode after inactivity
  const inactivityTimer = useRef(null);
  const INACTIVITY_TIMEOUT = 60000; // 60 seconds

  // Ref for barcode input to manage focus
  const barcodeInputRef = useRef(null);

  // Comprehensive state reset function
  const resetSessionState = useCallback(() => {
    // Reset all state except selectedWarehouse (which should persist)
    setMode('');
    setCurrentLocation(null);
    setFoundPart(null);
    setQuantity(1);
    setDestinationLocation('');
    setSelectedTechnician('');
    setBarcodeInput('');
    setNotes('');
    setError(null);
    setBarcodeLoading(false);

    // Reset UPC dialog state
    setShowUpcDialog(false);
    setUpcDialogData({
      upc: '',
      suppliers: [],
      selectedSupplier: '',
      selectedPart: '',
      supplierParts: [],
      partId: null
    });

    // Clear any session storage except warehouse
    sessionStorage.removeItem('scan_location');

    // Show notification to user
    toast.info('Session reset due to inactivity. Please select mode and scan location.');

    // Reset focus to appropriate element after a short delay to ensure DOM updates
    setTimeout(() => {
      // If there's a barcode input visible, focus on it
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
  }, []);

  // Reset inactivity timer whenever there's user activity
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }

    // Always start the timer, regardless of current state
    // Timer should be active as long as a warehouse is selected
    if (selectedWarehouse) {
      inactivityTimer.current = setTimeout(() => {
        resetSessionState();
      }, INACTIVITY_TIMEOUT);
    }
  }, [selectedWarehouse, resetSessionState]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch warehouses for dropdown
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);

        // Fetch all locations for move mode destination selection
        const allLocationsData = await getLocations();
        setAllLocations(allLocationsData);

        // Fetch technicians for technician issue mode
        const techniciansData = await getTechnicians({ include_inactive: false });
        setTechnicians(techniciansData);

        // Get saved session data if available
        const savedWarehouse = sessionStorage.getItem('scan_warehouse');

        if (savedWarehouse) {
          setSelectedWarehouse(savedWarehouse);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();

    // Cleanup timer on component unmount
    return () => {
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, []);

  // Set up user interaction event listeners
  useEffect(() => {
    // Add comprehensive user interaction event listeners
    const handleUserInteraction = () => {
      // Only reset timer if warehouse is selected
      if (selectedWarehouse) {
        resetInactivityTimer();
      }
    };

    // Listen for various user interaction events
    const events = ['mousedown', 'mousemove', 'keypress', 'keydown', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, true);
    });

    // Cleanup event listeners
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction, true);
      });
    };
  }, [selectedWarehouse, resetInactivityTimer]);

  // Start/restart timer when warehouse is selected
  useEffect(() => {
    if (selectedWarehouse) {
      resetInactivityTimer();
    } else {
      // Clear timer if no warehouse selected
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    }
  }, [selectedWarehouse, resetInactivityTimer]);

  // Handle warehouse change
  const handleWarehouseChange = async (e) => {
    const warehouseId = e.target.value;
    setSelectedWarehouse(warehouseId);
    sessionStorage.setItem('scan_warehouse', warehouseId);
    setCurrentLocation(null); // Reset location when warehouse changes
    sessionStorage.removeItem('scan_location');

    resetInactivityTimer();
  };

  // Handle mode selection
  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    setDestinationLocation(''); // Clear destination when mode changes
    setSelectedTechnician(''); // Clear technician when mode changes
    resetInactivityTimer();
  };

  // Handle barcode scan
  const handleBarcodeScan = async () => {
    if (!barcodeInput) return;

    try {
      setBarcodeLoading(true);
      setError(null);
      setFoundPart(null);

      const result = await getItemByBarcode(barcodeInput);

      if (result.type === 'location') {
        // If barcode is for a location
        // Check if location is in the selected warehouse
        if (selectedWarehouse && result.location.warehouse_id !== parseInt(selectedWarehouse)) {
          toast.error(`Location ${result.location.rack}-${result.location.shelf} is in ${result.location.warehouse?.name}, not the selected warehouse`);
          setBarcodeInput('');
          setBarcodeLoading(false);
          return;
        }

        setCurrentLocation(result.location);
        setFoundPart(null);
        toast.success(`Location: ${result.location.warehouse?.name} - ${result.location.rack}-${result.location.shelf}`);
        setBarcodeInput('');
      } else if (result.type === 'part') {
        // If barcode is for a part
        if (!currentLocation) {
          toast.error('Please scan a location first');
          setBarcodeInput('');
          setBarcodeLoading(false);
          return;
        }

        if (!mode) {
          toast.error('Please select an operation mode (Add/Issue/Move) first');
          setBarcodeInput('');
          setBarcodeLoading(false);
          return;
        }

        setFoundPart(result.part);
        toast.success(`Found part: ${result.part.internal_part_number}`);
        processInventoryAdjustment(result.part.id);
      } else {
        toast.error('Barcode not recognized');
        handleUpcDialogOpen(barcodeInput);
      }

      setBarcodeLoading(false);
    } catch (err) {
      console.error('Error searching barcode:', err);

      // If barcode not found, determine what type of error to show based on context
      if (err.message === 'No item found with this barcode') {
        if (!currentLocation) {
          // No location selected - assume they're trying to scan a location
          toast.error('Location barcode not recognized. Please verify the barcode and try again.');
        } else {
          // Location is selected - assume they're trying to scan a part
          toast.error('Part barcode not recognized.');
          // Offer to add as UPC since they're scanning parts
          handleUpcDialogOpen(barcodeInput);
        }
      } else {
        setError('Error scanning barcode. Please try again.');
      }

      setBarcodeLoading(false);
    }

    resetInactivityTimer();
  };

  // Process inventory adjustment after scanning
  const processInventoryAdjustment = async (partId) => {
    if (!currentLocation || !mode) {
      toast.error('Please scan a location and select a mode first');
      return;
    }

    if (mode === 'move' && !destinationLocation) {
      toast.error('Please select a destination location for move operation');
      return;
    }

    if (mode === 'move' && currentLocation.id === parseInt(destinationLocation)) {
      toast.error('Source and destination locations must be different');
      return;
    }

    if (mode === 'issue_to_technician' && !selectedTechnician) {
      toast.error('Please select a technician for issue operation');
      return;
    }

    try {
      if (mode === 'move') {
        // Handle move operation
        const moveData = {
          part_id: partId,
          from_location_id: currentLocation.id,
          to_location_id: destinationLocation,
          quantity: quantity,
          notes: notes || 'Scanned move operation'
        };

        await moveInventory(moveData);
        toast.success(`Inventory moved successfully`);
      } else if (mode === 'issue_to_technician') {
        // Handle technician issue operation
        const technicianIssueData = {
          technician_id: selectedTechnician,
          part_id: partId,
          location_id: currentLocation.id,
          quantity: quantity,
          notes: notes || 'Scanned issue to technician'
        };

        await issueInventoryToTechnician(technicianIssueData);
        const technicianName = technicians.find(t => t.id === parseInt(selectedTechnician))?.name || 'technician';
        toast.success(`Inventory issued to ${technicianName} successfully`);
      } else {
        // Handle add/issue operations
        const adjustmentData = {
          part_id: partId,
          location_id: currentLocation.id,
          transaction_type: mode === 'add' ? 'receive' : 'issue',
          quantity_change: mode === 'add' ? quantity : -quantity,
          notes: notes || `Scanned ${mode === 'add' ? 'in' : 'out'}`
        };

        await adjustInventory(adjustmentData);
        toast.success(`Inventory ${mode === 'add' ? 'added' : 'issued'} successfully`);
      }

      setBarcodeInput('');
      setQuantity(1);
      setFoundPart(null);
    } catch (err) {
      console.error('Error processing inventory:', err);
      setError(err.message || 'Failed to process inventory');
    }

    resetInactivityTimer();
  };

  // Fetch suppliers for UPC dialog
  const fetchSuppliers = async () => {
    try {
      const suppliersData = await getSuppliers();
      setUpcDialogData(prev => ({
        ...prev,
        suppliers: suppliersData
      }));
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      toast.error('Failed to load suppliers');
    }
  };

  // Handle UPC dialog open
  const handleUpcDialogOpen = (upcCode) => {
    setUpcDialogData(prev => ({
      ...prev,
      upc: upcCode,
      selectedSupplier: '',
      selectedPart: '',
      supplierParts: [],
      partId: null
    }));
    setShowUpcDialog(true);
    fetchSuppliers();
  };

  // Handle UPC dialog close
  const handleUpcDialogClose = () => {
    setShowUpcDialog(false);
    setBarcodeInput('');
  };

  // Handle supplier change
  const handleSupplierChange = async (e) => {
    const supplierId = e.target.value;

    setUpcDialogData(prev => ({
      ...prev,
      selectedSupplier: supplierId,
      selectedPart: '',
      supplierParts: [],
      partId: null
    }));

    if (supplierId) {
      try {
        const partsData = await getSupplierParts(supplierId);
        setUpcDialogData(prev => ({
          ...prev,
          supplierParts: partsData
        }));
      } catch (err) {
        console.error('Error fetching supplier parts:', err);
        toast.error('Failed to load supplier parts');
      }
    }
  };



  // Handle adding UPC to a part
  const handleAddUpc = async () => {
    if (!upcDialogData.selectedPart) {
      toast.error('Please select a part first');
      return;
    }

    try {
      // First associate the UPC with the supplier part
      await associateUpc({
        upc_code: upcDialogData.upc,
        supplier_part_id: upcDialogData.selectedPart,
        is_primary: true
      });

      toast.success('UPC code added successfully');

      // Now process the inventory adjustment if we have a part ID and location
      if (upcDialogData.partId && currentLocation && mode) {
        await processInventoryAdjustment(upcDialogData.partId);
      } else if (!currentLocation || !mode) {
        toast.warning('UPC added but no inventory adjusted. Please scan a location and select a mode first.');
      }

      setShowUpcDialog(false);
      setBarcodeInput('');
    } catch (err) {
      console.error('Error adding UPC code:', err);
      setError(err.message || 'Failed to add UPC code');
    }
  };

  return (
    <Container>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Scan Inventory
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={() => setBundlePrintDialogOpen(true)}
        >
          Print Bundle Labels
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Warehouse Selection */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Warehouse Selection
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Warehouse</InputLabel>
                  <Select
                    value={selectedWarehouse}
                    onChange={handleWarehouseChange}
                    label="Warehouse"
                  >
                    <MenuItem value="">
                      <em>Select Warehouse</em>
                    </MenuItem>
                    {warehouses.map(warehouse => (
                      <MenuItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Current Location Display */}
          {selectedWarehouse && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Current Location
              </Typography>
              {currentLocation ? (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`${currentLocation.warehouse?.name} - ${currentLocation.rack}-${currentLocation.shelf}`}
                    color="primary"
                    sx={{ fontSize: '1.1rem', py: 2, px: 1 }}
                  />
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No location selected. Scan a location barcode to begin.
                </Typography>
              )}
            </Paper>
          )}

          {/* Mode Selection */}
          {selectedWarehouse ? (
            <Paper sx={{ p: 3, mb: 3 }}>
              {!mode ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Select Operation
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      startIcon={<AddIcon />}
                      onClick={() => handleModeSelect('add')}
                      sx={{ py: 2, px: 4 }}
                    >
                      Add Inventory
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="large"
                      startIcon={<RemoveIcon />}
                      onClick={() => handleModeSelect('issue')}
                      sx={{ py: 2, px: 4 }}
                    >
                      Issue Inventory
                    </Button>
                    <Button
                      variant="contained"
                      color="info"
                      size="large"
                      startIcon={<MoveIcon />}
                      onClick={() => handleModeSelect('move')}
                      sx={{ py: 2, px: 4 }}
                    >
                      Move Inventory
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      size="large"
                      startIcon={<PersonIcon />}
                      onClick={() => handleModeSelect('issue_to_technician')}
                      sx={{ py: 2, px: 4 }}
                    >
                      Issue to Technician
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      {mode === 'add' ? 'Adding' : mode === 'issue' ? 'Issuing' : mode === 'move' ? 'Moving' : 'Issuing to Technician'} Inventory
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setMode('')}
                    >
                      Change Mode
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Destination Location Selection for Move Mode */}
                  {mode === 'move' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Destination Location
                      </Typography>
                      <FormControl fullWidth required>
                        <InputLabel>Select Destination Location</InputLabel>
                        <Select
                          value={destinationLocation}
                          onChange={(e) => setDestinationLocation(e.target.value)}
                          label="Select Destination Location"
                        >
                          <MenuItem value="">
                            <em>Select Destination Location</em>
                          </MenuItem>
                          {allLocations
                            .filter(location => !currentLocation || location.id !== currentLocation.id) // Exclude source location
                            .map(location => (
                              <MenuItem key={location.id} value={location.id}>
                                {location.warehouse?.name} - {location.rack}-{location.shelf}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Technician Selection for Issue to Technician Mode */}
                  {mode === 'issue_to_technician' && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Select Technician
                      </Typography>
                      <FormControl fullWidth required>
                        <InputLabel>Select Technician</InputLabel>
                        <Select
                          value={selectedTechnician}
                          onChange={(e) => setSelectedTechnician(e.target.value)}
                          label="Select Technician"
                        >
                          <MenuItem value="">
                            <em>Select Technician</em>
                          </MenuItem>
                          {technicians.map(technician => (
                            <MenuItem key={technician.id} value={technician.id}>
                              {technician.name} ({technician.type})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  {/* Barcode Scanner */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {!currentLocation ? 'Scan Location Barcode' : 'Scan Part Barcode'}
                    </Typography>
                    {!currentLocation && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        First, scan a location barcode to set your current location.
                      </Typography>
                    )}
                    {currentLocation && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Now scan part barcodes to {mode === 'add' ? 'add' : mode === 'issue' ? 'issue' : mode === 'move' ? 'move' : 'issue to technician'} inventory.
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ position: 'relative', width: '100%' }}>
                        <TextField
                          fullWidth
                          label="Scan or Enter Barcode"
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleBarcodeScan();
                            }
                          }}
                          autoFocus
                          inputRef={barcodeInputRef}
                        />
                        <Box sx={{ position: 'absolute', right: 8, top: 8 }}>
                          {barcodeLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <IconButton
                              onClick={handleBarcodeScan}
                              disabled={!barcodeInput}
                              edge="end"
                            >
                              <SearchIcon />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        onClick={handleBarcodeScan}
                        disabled={barcodeLoading || !barcodeInput}
                        sx={{ height: 56 }}
                      >
                        Scan
                      </Button>
                    </Box>
                  </Box>

                  {/* Quantity Input */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Quantity
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                      >
                        -
                      </Button>
                      <Typography variant="body1" sx={{ minWidth: '30px', textAlign: 'center' }}>
                        {quantity}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setQuantity(quantity + 1)}
                      >
                        +
                      </Button>
                    </Box>
                  </Box>

                  {/* Notes */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Notes (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Box>
                </>
              )}
            </Paper>
          ) : (
            <Alert severity="info" sx={{ mb: 3 }}>
              Please select a warehouse to continue.
            </Alert>
          )}

          {/* Part Information (if found) */}
          {foundPart && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Part Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  Part Number: {foundPart.internal_part_number}
                </Typography>
                <Typography variant="body1">
                  Description: {foundPart.description}
                </Typography>
              </Box>
            </Paper>
          )}
        </>
      )}

      {/* Dialog for adding UPC to a part */}
      <Dialog open={showUpcDialog} onClose={handleUpcDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Associate UPC with Supplier Part</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The barcode "{upcDialogData.upc}" was not recognized. Please select a supplier part to associate this UPC code with:
          </DialogContentText>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select a supplier and part:
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Supplier</InputLabel>
              <Select
                value={upcDialogData.selectedSupplier}
                onChange={handleSupplierChange}
                label="Supplier"
              >
                <MenuItem value="">
                  <em>Select Supplier</em>
                </MenuItem>
                {upcDialogData.suppliers.map(supplier => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={!upcDialogData.selectedSupplier || upcDialogData.supplierParts.length === 0}>
              <InputLabel>Part</InputLabel>
              <Select
                value={upcDialogData.selectedPart}
                onChange={(e) => {
                  const supplierPartId = e.target.value;
                  const selectedSupplierPart = upcDialogData.supplierParts.find(part => part.id === supplierPartId);

                  setUpcDialogData({
                    ...upcDialogData,
                    selectedPart: supplierPartId,
                    partId: selectedSupplierPart ? selectedSupplierPart.part_id : null
                  });
                }}
                label="Part"
              >
                <MenuItem value="">
                  <em>Select Part</em>
                </MenuItem>
                {upcDialogData.supplierParts.map(part => (
                  <MenuItem key={part.id} value={part.id}>
                    {part.part.internal_part_number} - {part.part.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpcDialogClose}>Cancel</Button>
          <Button
            onClick={handleAddUpc}
            color="primary"
            disabled={!upcDialogData.selectedPart}
          >
            Add UPC
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle Print Dialog */}
      <BundlePrintDialog
        open={bundlePrintDialogOpen}
        onClose={() => setBundlePrintDialogOpen(false)}
      />
    </Container>
  );
};

export default Scan;
